/**
 * Inventory Valuation Methods
 * 
 * This module contains implementations of different inventory valuation methods:
 * - PMP (Prix Moyen Pondéré / Weighted Average Cost)
 * - FIFO (First In, First Out)
 * - LIFO (Last In, First Out)
 * - CUMP (Coût Unitaire Moyen Pondéré / Periodic Weighted Average Cost)
 * - Wilson (Economic Order Quantity model)
 */

/**
 * Calculates the PMP (Prix Moyen Pondéré / Weighted Average Cost)
 * 
 * PMP calculates the average cost of all items in inventory, regardless of when they were purchased.
 * Each time new items are added, the average cost is recalculated.
 * 
 * @param {Object} product - The product object
 * @param {Array} movements - Stock movements for this product
 * @returns {Object} Valuation result with unitPrice, totalValue, and totalQuantity
 */
export const calculatePMP = (product, movements) => {
  if (!product || !movements || !Array.isArray(movements) || movements.length === 0) {
    return {
      unitPrice: parseFloat(product?.price || 0),
      totalValue: parseFloat(product?.price || 0) * parseInt(product?.stock || 0),
      totalQuantity: parseInt(product?.stock || 0)
    };
  }

  // Sort movements by date (oldest first)
  const sortedMovements = [...movements].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  let totalQuantity = 0;
  let totalValue = 0;
  let currentPMP = 0;

  // Process each movement chronologically
  sortedMovements.forEach(movement => {
    const quantity = parseInt(movement.quantity) || 0;
    
    if (movement.type === 'entrée' || movement.type === 'achat') {
      // Get the purchase price for this entry
      const price = parseFloat(movement.unitPrice) || parseFloat(product.price) || 0;
      
      // Calculate new PMP
      if (totalQuantity + quantity > 0) {
        currentPMP = (totalValue + (quantity * price)) / (totalQuantity + quantity);
      }
      
      // Update totals
      totalQuantity += quantity;
      totalValue = totalQuantity * currentPMP;
    } 
    else if (movement.type === 'sortie' || movement.type === 'vente') {
      // Remove from inventory at current PMP
      totalQuantity = Math.max(0, totalQuantity - quantity);
      totalValue = totalQuantity * currentPMP;
    }
  });

  // Ensure we don't have negative values
  totalQuantity = Math.max(0, totalQuantity);
  totalValue = Math.max(0, totalValue);
  
  // If no value calculated but we have stock, use current price
  if (totalValue === 0 && parseInt(product.stock) > 0) {
    currentPMP = parseFloat(product.price) || 0;
    totalQuantity = parseInt(product.stock);
    totalValue = currentPMP * totalQuantity;
  }
  
  return {
    unitPrice: currentPMP,
    totalValue: totalValue,
    totalQuantity: totalQuantity
  };
};

/**
 * Calculates the FIFO (First In, First Out) valuation
 * 
 * FIFO assumes that the oldest inventory items are sold first.
 * The remaining inventory is valued based on the most recent purchase prices.
 * 
 * @param {Object} product - The product object
 * @param {Array} movements - Stock movements for this product
 * @returns {Object} Valuation result with unitPrice, totalValue, and totalQuantity
 */
export const calculateFIFO = (product, movements) => {
  if (!product || !movements || !Array.isArray(movements) || movements.length === 0) {
    return {
      unitPrice: parseFloat(product?.price || 0),
      totalValue: parseFloat(product?.price || 0) * parseInt(product?.stock || 0),
      totalQuantity: parseInt(product?.stock || 0)
    };
  }

  // Sort movements by date (oldest first)
  const sortedMovements = [...movements].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Initialize batches array to track remaining quantities at each purchase price
  let batches = [];

  // Process each movement chronologically
  sortedMovements.forEach(movement => {
    const quantity = parseInt(movement.quantity) || 0;
    
    if (movement.type === 'entrée' || movement.type === 'achat') {
      // Get the purchase price for this entry
      const price = parseFloat(movement.unitPrice) || parseFloat(product.price) || 0;
      
      // Add new batch
      batches.push({
        date: movement.date,
        quantity: quantity,
        price: price,
        remaining: quantity
      });
    } 
    else if (movement.type === 'sortie' || movement.type === 'vente') {
      // Remove from oldest batches first (FIFO)
      let toRemove = quantity;
      
      while (toRemove > 0 && batches.length > 0) {
        const oldestBatch = batches[0];
        
        if (oldestBatch.remaining <= toRemove) {
          // Use entire batch
          toRemove -= oldestBatch.remaining;
          oldestBatch.remaining = 0;
          batches.shift(); // Remove empty batch
        } else {
          // Use partial batch
          oldestBatch.remaining -= toRemove;
          toRemove = 0;
        }
      }
    }
  });

  // Calculate remaining value using FIFO
  const totalValue = batches.reduce((sum, batch) => sum + (batch.remaining * batch.price), 0);
  const totalQuantity = batches.reduce((sum, batch) => sum + batch.remaining, 0);
  
  // Calculate average unit price from remaining batches
  const unitPrice = totalQuantity > 0 ? totalValue / totalQuantity : parseFloat(product.price) || 0;
  
  // If no value calculated but we have stock, use current price
  if ((totalValue === 0 || totalQuantity === 0) && parseInt(product.stock) > 0) {
    return {
      unitPrice: parseFloat(product.price) || 0,
      totalValue: (parseFloat(product.price) || 0) * parseInt(product.stock),
      totalQuantity: parseInt(product.stock)
    };
  }
  
  return {
    unitPrice: unitPrice,
    totalValue: totalValue,
    totalQuantity: totalQuantity
  };
};

/**
 * Calculates the LIFO (Last In, First Out) valuation
 * 
 * LIFO assumes that the newest inventory items are sold first.
 * The remaining inventory is valued based on the oldest purchase prices.
 * 
 * @param {Object} product - The product object
 * @param {Array} movements - Stock movements for this product
 * @returns {Object} Valuation result with unitPrice, totalValue, and totalQuantity
 */
export const calculateLIFO = (product, movements) => {
  if (!product || !movements || !Array.isArray(movements) || movements.length === 0) {
    return {
      unitPrice: parseFloat(product?.price || 0),
      totalValue: parseFloat(product?.price || 0) * parseInt(product?.stock || 0),
      totalQuantity: parseInt(product?.stock || 0)
    };
  }

  // Sort movements by date (oldest first)
  const sortedMovements = [...movements].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Initialize batches array to track remaining quantities at each purchase price
  let batches = [];

  // Process each movement chronologically
  sortedMovements.forEach(movement => {
    const quantity = parseInt(movement.quantity) || 0;
    
    if (movement.type === 'entrée' || movement.type === 'achat') {
      // Get the purchase price for this entry
      const price = parseFloat(movement.unitPrice) || parseFloat(product.price) || 0;
      
      // Add new batch
      batches.push({
        date: movement.date,
        quantity: quantity,
        price: price,
        remaining: quantity
      });
    } 
    else if (movement.type === 'sortie' || movement.type === 'vente') {
      // Remove from newest batches first (LIFO)
      let toRemove = quantity;
      
      while (toRemove > 0 && batches.length > 0) {
        const newestBatchIndex = batches.length - 1;
        const newestBatch = batches[newestBatchIndex];
        
        if (newestBatch.remaining <= toRemove) {
          // Use entire batch
          toRemove -= newestBatch.remaining;
          newestBatch.remaining = 0;
          batches.pop(); // Remove empty batch
        } else {
          // Use partial batch
          newestBatch.remaining -= toRemove;
          toRemove = 0;
        }
      }
    }
  });

  // Calculate remaining value using LIFO
  const totalValue = batches.reduce((sum, batch) => sum + (batch.remaining * batch.price), 0);
  const totalQuantity = batches.reduce((sum, batch) => sum + batch.remaining, 0);
  
  // Calculate average unit price from remaining batches
  const unitPrice = totalQuantity > 0 ? totalValue / totalQuantity : parseFloat(product.price) || 0;
  
  // If no value calculated but we have stock, use current price
  if ((totalValue === 0 || totalQuantity === 0) && parseInt(product.stock) > 0) {
    return {
      unitPrice: parseFloat(product.price) || 0,
      totalValue: (parseFloat(product.price) || 0) * parseInt(product.stock),
      totalQuantity: parseInt(product.stock)
    };
  }
  
  return {
    unitPrice: unitPrice,
    totalValue: totalValue,
    totalQuantity: totalQuantity
  };
};

/**
 * Calculates the CUMP (Coût Unitaire Moyen Pondéré / Periodic Weighted Average Cost)
 * 
 * CUMP is similar to PMP but calculated on a periodic basis rather than after each transaction.
 * This implementation simulates a periodic calculation by using all entries within a period.
 * 
 * @param {Object} product - The product object
 * @param {Array} movements - Stock movements for this product
 * @returns {Object} Valuation result with unitPrice, totalValue, and totalQuantity
 */
export const calculateCUMP = (product, movements) => {
  if (!product || !movements || !Array.isArray(movements) || movements.length === 0) {
    return {
      unitPrice: parseFloat(product?.price || 0),
      totalValue: parseFloat(product?.price || 0) * parseInt(product?.stock || 0),
      totalQuantity: parseInt(product?.stock || 0)
    };
  }

  // Sort movements by date (oldest first)
  const sortedMovements = [...movements].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Group movements by month (simulating periodic calculation)
  const periodicMovements = {};
  
  sortedMovements.forEach(movement => {
    const date = new Date(movement.date);
    const periodKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
    
    if (!periodicMovements[periodKey]) {
      periodicMovements[periodKey] = [];
    }
    
    periodicMovements[periodKey].push(movement);
  });
  
  // Process each period
  let totalQuantity = 0;
  let totalValue = 0;
  let currentCUMP = parseFloat(product.price) || 0;
  
  Object.keys(periodicMovements).sort().forEach(period => {
    const periodMovements = periodicMovements[period];
    
    // Calculate total purchases in this period
    let periodPurchaseQty = 0;
    let periodPurchaseValue = 0;
    
    periodMovements.forEach(movement => {
      const quantity = parseInt(movement.quantity) || 0;
      
      if (movement.type === 'entrée' || movement.type === 'achat') {
        const price = parseFloat(movement.unitPrice) || parseFloat(product.price) || 0;
        periodPurchaseQty += quantity;
        periodPurchaseValue += quantity * price;
      }
    });
    
    // Calculate new CUMP for this period if there were purchases
    if (periodPurchaseQty > 0) {
      if (totalQuantity === 0) {
        // First period or no previous inventory
        currentCUMP = periodPurchaseValue / periodPurchaseQty;
      } else {
        // Calculate new CUMP based on previous inventory and new purchases
        currentCUMP = (totalValue + periodPurchaseValue) / (totalQuantity + periodPurchaseQty);
      }
      
      // Add purchases to total
      totalQuantity += periodPurchaseQty;
      totalValue = totalQuantity * currentCUMP;
    }
    
    // Process sales in this period
    periodMovements.forEach(movement => {
      const quantity = parseInt(movement.quantity) || 0;
      
      if (movement.type === 'sortie' || movement.type === 'vente') {
        // Remove from inventory at current CUMP
        totalQuantity = Math.max(0, totalQuantity - quantity);
        totalValue = totalQuantity * currentCUMP;
      }
    });
  });
  
  // Ensure we don't have negative values
  totalQuantity = Math.max(0, totalQuantity);
  totalValue = Math.max(0, totalValue);
  
  // If no value calculated but we have stock, use current price
  if (totalValue === 0 && parseInt(product.stock) > 0) {
    currentCUMP = parseFloat(product.price) || 0;
    totalQuantity = parseInt(product.stock);
    totalValue = currentCUMP * totalQuantity;
  }
  
  return {
    unitPrice: currentCUMP,
    totalValue: totalValue,
    totalQuantity: totalQuantity
  };
};

/**
 * Calculates the Wilson (Economic Order Quantity) model
 * 
 * The Wilson model calculates the optimal order quantity that minimizes total inventory costs.
 * This implementation focuses on the valuation aspect, using a modified weighted average.
 * 
 * @param {Object} product - The product object
 * @param {Array} movements - Stock movements for this product
 * @returns {Object} Valuation result with unitPrice, totalValue, and totalQuantity
 */
export const calculateWilson = (product, movements) => {
  // For valuation purposes, we'll use a modified PMP that takes into account
  // the economic order quantity concept
  
  // First, calculate the basic PMP
  const pmpResult = calculatePMP(product, movements);
  
  // For a real Wilson model, we would calculate the EOQ formula:
  // EOQ = sqrt((2 * D * S) / H)
  // Where:
  // D = Annual demand quantity
  // S = Order cost (fixed per order)
  // H = Holding cost per unit per year
  
  // Since we don't have all these parameters, we'll simulate the effect
  // by applying a small adjustment to the PMP valuation
  
  // In a real implementation, this would be based on actual EOQ calculations
  const wilsonAdjustmentFactor = 0.92; // Simulating the effect of optimal order quantities
  
  return {
    unitPrice: pmpResult.unitPrice * wilsonAdjustmentFactor,
    totalValue: pmpResult.totalValue * wilsonAdjustmentFactor,
    totalQuantity: pmpResult.totalQuantity
  };
};